// frontend/src/state/useChartKnobsState.ts

import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { availableFacetsForChartType } from '~/components/Charts/facets'

export type Facet = 'line' | 'stacked_area' | 'bar' | 'stacked_bar'
export type ChartType = 'timeline' | 'absolute_bar' | 'wrapper'

const FACET_TO_CHAR: Record<Facet, string> = {
  line: 'L',
  stacked_area: 'A',
  bar: 'B',
  stacked_bar: 'S',
}
const CHAR_TO_FACET = Object.fromEntries(
  Object.entries(FACET_TO_CHAR).map(([k, v]) => [v, k as Facet]),
) as Record<string, Facet>

export const STACKED_FACETS: Facet[] = ['stacked_area', 'stacked_bar']

export interface ChartKnobsState {
  facet: Facet
  cumulative: boolean
  showPercentage: boolean
  inactiveSeries: string[]
  runningTotal: boolean
  setFacet: (f: Facet) => void
  setCumulative: (c: boolean) => void
  setShowPercentage: (p: boolean) => void
  setInactiveSeries: (s: string[]) => void
  setRunningTotal: (rt: boolean) => void
}

function baseFacet(chartType: ChartType, override?: string | null): Facet {
  if (
    override &&
    (availableFacetsForChartType(chartType) as readonly string[]).includes(override)
  ) {
    return override as Facet
  }
  return chartType === 'absolute_bar' ? 'bar' : 'line'
}

function encode(facet: Facet, cum: boolean, pct: boolean, off: string[], rt: boolean): string {
  const offStr = off.map(encodeURIComponent).join(',')
  return `${FACET_TO_CHAR[facet]}:${cum ? 1 : 0}:${pct ? 1 : 0}:${offStr}:${rt ? 1 : 0}`
}

interface Decoded {
  facet: Facet
  cum: boolean
  pct: boolean
  off: string[]
  rt: boolean
}

function decode(raw: string | null, chartType: ChartType, override?: string | null): Decoded {
  if (raw === null) {
    const facet = baseFacet(chartType, override)
    return {
      facet,
      cum: STACKED_FACETS.includes(facet),
      pct: false,
      off: [],
      rt: false,
    }
  }
  const [facetChar = 'L', cumChar = '0', pctChar = '0', offRaw = '', rtChar = '0'] = raw.split(':')
  const facet = CHAR_TO_FACET[facetChar] ?? baseFacet(chartType, override)
  return {
    facet,
    cum: cumChar === '1',
    pct: pctChar === '1',
    off: offRaw === '' ? [] : offRaw.split(',').map(decodeURIComponent),
    rt: rtChar === '1',
  }
}

function isDefault(d: Decoded, chartType: ChartType, override?: string | null): boolean {
  const f = baseFacet(chartType, override)
  return (
    d.facet === f &&
    d.cum === STACKED_FACETS.includes(f) &&
    d.pct === false &&
    d.off.length === 0 &&
    d.rt === false
  )
}

export function useChartKnobsState(
  pinId: string,
  chartType: ChartType,
  defaultFacet?: string | null,
): ChartKnobsState {
  const [searchParams, setSearchParams] = useSearchParams()
  const key = `c_${pinId}`
  const raw = searchParams.get(key)

  const decoded = useMemo(
    () => decode(raw, chartType, defaultFacet),
    [raw, chartType, defaultFacet],
  )

  const writeRaw = useCallback(
    (next: Decoded, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (isDefault(next, chartType, defaultFacet)) {
            params.delete(key)
          } else {
            params.set(key, encode(next.facet, next.cum, next.pct, next.off, next.rt))
          }
          return params
        },
        // User gestures push (default); reconcile writers pass replace (Bug-1b hygiene).
        { replace: opts?.replace ?? false },
      )
    },
    [setSearchParams, key, chartType, defaultFacet],
  )

  // Auto-cumulative effect: when facet flips to a stacked variant, cumulative is forced true.
  // Repair, not gesture — must not add a Back-stack entry (Bug-1b hygiene rider).
  useEffect(() => {
    if (STACKED_FACETS.includes(decoded.facet) && !decoded.cum) {
      writeRaw({ ...decoded, cum: true }, { replace: true })
    }
  }, [decoded, writeRaw])

  const setFacet = useCallback(
    (f: Facet) => {
      const cum = STACKED_FACETS.includes(f) ? true : decoded.cum
      writeRaw({ ...decoded, facet: f, cum })
    },
    [decoded, writeRaw],
  )
  const setCumulative = useCallback(
    (c: boolean) => writeRaw({ ...decoded, cum: c }),
    [decoded, writeRaw],
  )
  const setShowPercentage = useCallback(
    (p: boolean) => writeRaw({ ...decoded, pct: p }),
    [decoded, writeRaw],
  )
  const setInactiveSeries = useCallback(
    (s: string[]) => writeRaw({ ...decoded, off: s }),
    [decoded, writeRaw],
  )
  const setRunningTotal = useCallback(
    (rt: boolean) => writeRaw({ ...decoded, rt }),
    [decoded, writeRaw],
  )

  return {
    facet: decoded.facet,
    cumulative: decoded.cum,
    showPercentage: decoded.pct,
    inactiveSeries: decoded.off,
    runningTotal: decoded.rt,
    setFacet,
    setCumulative,
    setShowPercentage,
    setInactiveSeries,
    setRunningTotal,
  }
}
